// El catálogo: todos los cursos publicados, en una sola lista y ordenados para elegir.
//
//     node scripts/prueba_catalogo.mjs        (desde frontend/)
//
// EL AGUJERO QUE TAPA, y lo dijo Gabri el 26 de septiembre de 2026: *«la landing dice 22 pero
// no dice cuáles»*. Para ver los veintidós cursos había que entrar en `/cursos`, elegir una de
// las cinco competencias, mirar su lista, volver, elegir otra… cinco veces. Y eso **se lo pides
// justo a quien menos puede hacerlo**: alguien que ya sabe que lo suyo es la Filosofía entra
// directo; el que llega sin saber qué estudiar es el único que necesita verlo todo junto, y era
// al único al que se le escondía.
//
// UNA CIFRA QUE NO SE PUEDE ABRIR ES PEOR QUE NO PONERLA. «22 cursos» sin decir cuáles pide un
// acto de fe, y en una plataforma que nadie conoce todavía, el que llega no lo hace.
//
// QUÉ ORDENA ESTO Y QUÉ NO. Aquí se junta, se cuenta, se busca y se filtra; **lo que hay** lo
// dice el servidor y nada más que el servidor (`/api/cursos-publicados`). Si mañana se publica
// un curso, aparece aquí sin tocar una línea. Escribir la lista en el navegador sería la avería
// con nombre de esta casa: dos sitios que dicen lo mismo y un día dejan de decirlo.
//
// Lógica pura: ni React ni peticiones.

/**
 * PARA BUSCAR SIN TILDES NI MAYÚSCULAS. Quien escribe «musica» tiene que encontrar «Música», y
 * quien escribe «Ética» tiene que encontrar «ética»: en un buscador de una página en español,
 * exigir la tilde es exigir que el que busca ya sepa cómo se escribe lo que busca.
 *
 * VIVE AQUÍ Y NO EN LA PÁGINA porque ahora lo usan dos: el filtro de las competencias y el del
 * catálogo. Escrito dos veces, un día uno de los dos deja de quitar las tildes y nadie se
 * entera hasta que alguien busca «Ética».
 *
 * Y LA EÑE TAMBIÉN SE DESHACE —«espana» encuentra «España»—, que no es un descuido: es lo que
 * hace falta en un teclado que no la tiene. Queda dicho porque es justo lo que alguien
 * «arreglaría» un día sin saber que lo estaba rompiendo.
 */
import { BLOQUES, familiaDe } from "../data/emojisDeFamilia.mjs";
import { claveDe, grupoDe } from "./cursosDelTest.mjs";

export const llano = (t) => String(t || "")
  .toLowerCase()
  .normalize("NFD")
  .replace(/[̀-ͯ]/g, "");

/**
 * TODOS LOS CURSOS EN UNA LISTA, con su competencia puesta encima.
 *
 * `porCompetencia` es lo que contesta el servidor: `{competencia: [{id, titulo, ...}]}`.
 * `competencias` es la lista de `data/courses.js`, que es quien sabe cómo se llama cada una de cara al
 * alumno —«filosofia» es «Pensamiento crítico»— y en qué orden se presentan.
 *
 * EL ORDEN ES EL DE LAS COMPETENCIAS Y NO EL ALFABÉTICO, a propósito: es el mismo que las cinco
 * tarjetas de arriba. Una lista ordenada de otra manera que lo que está justo encima obliga a
 * leerla entera para encontrar algo que ya se había visto.
 */
export function todosLosCursos(deCadaCompetencia, competencias) {
  const fuera = [];
  (competencias || []).forEach((c) => {
    ((deCadaCompetencia || {})[c.slug] || []).forEach((curso) => {
      if (!curso || !curso.titulo) return;
      fuera.push({
        ...curso,
        etapa: curso.etapa || [],
        lecciones: Number(curso.lecciones) || 0,
        competencia: c.slug,
        nombreCompetencia: c.nombreCompetencia,
      });
    });
  });
  return fuera;
}

/**
 * LOS CURSOS DEL CMS, EN LA FORMA DEL CATÁLOGO, para que se busquen y se agrupen con estas mismas
 * funciones. Gabri, 3 de noviembre de 2026: *«quiero la misma estructura de busqueda que sirve la
 * plataforma en cursos en del cms»*. Se añaden `titulo` y `lecciones` y se deja lo demás, que la
 * ficha del CMS sigue hablando de `title` y de `status`.
 *
 * AQUÍ NO SE ESCONDE NADA, que es lo que lo separa del catálogo del alumno: los borradores salen,
 * y un curso de una competencia desconocida sale al final con su nombre crudo. Quien edita tiene
 * que poder encontrar lo que el alumno no ve.
 */
export function cursosDelCMS(cursos, competencias) {
  const deCada = {};
  (cursos || []).forEach((c) => {
    const clave = c.competencia || "";
    (deCada[clave] = deCada[clave] || []).push({
      ...c, titulo: c.title || "Sin título", lecciones: c.lesson_count,
    });
  });
  const conocidas = new Set((competencias || []).map((c) => c.slug));
  const raras = Object.keys(deCada).filter((s) => !conocidas.has(s))
    .map((slug) => ({ slug, nombreCompetencia: slug || "Sin competencia" }));
  return todosLosCursos(deCada, [...(competencias || []), ...raras]);
}

/** «2 borradores» en la tarjeta de su familia, para verlos sin abrirla. Vacío si no hay ninguno. */
export function notaDeBorradores(cursos) {
  const n = (cursos || []).filter((c) => c.status !== "published").length;
  if (!n) return "";
  return n === 1 ? "1 borrador" : `${n} borradores`;
}

/**
 * LO QUE CASA CON LO QUE SE BUSCA, CON EL CURSO ESCOLAR Y CON LA COMPETENCIA.
 *
 * Y LAS TRES COSAS SE CRUZAN: se busca «poesía» estando en 3.º y salen los cursos de poesía de
 * 3.º, no todos los de poesía ni todo lo de 3.º. Filtros que no se cruzan son tres buscadores
 * distintos disfrazados de uno.
 *
 * LA COMPETENCIA ENTRA AQUÍ EL 18 DE OCTUBRE DE 2026, y hasta ese día no era un filtro: era una
 * página. Se pulsaba una de las cinco y se iba a `/cursos/<slug>` a ver sus cursos, que es lo
 * mismo que esta lista ya hace sin moverse de sitio. Gabri: *«al pulsar la competencia ya no se
 * debe mostrar una pantalla aparte con sus cursos, sino que se filtran los cursos de debajo»*.
 * **Dos piezas que hacen lo mismo con dos gestos distintos son dos cosas que aprender.**
 *
 * UN CURSO SIN ETAPA DECLARADA SALE SIEMPRE, y es la decisión que importa de esta función: es
 * un curso que no dice para quién es, no un curso que dice «para nadie». Escondiéndolo, el día
 * que a alguien se le olvide rellenar la ficha, el curso desaparece del catálogo sin que nadie
 * lo note —y no hay error que avise de un curso que no sale—.
 */
export function filtra(cursos, { busca = "", etapa = "", competencia = "" } = {}) {
  const q = llano(busca).trim();
  return (cursos || []).filter((c) => {
    // LA COMPETENCIA NO PERDONA AL QUE NO LA DECLARA, al revés que la etapa: un curso sin
    // etapa es un curso al que le falta la ficha, y esconderlo sería castigar al alumno por
    // un descuido nuestro; un curso sin competencia no existe —el catálogo se construye
    // recorriendo las cinco—, así que si alguno llegara sin ella, no es de ninguna.
    if (competencia && c.competencia !== competencia) return false;
    if (etapa && c.etapa.length && !c.etapa.includes(etapa)) return false;
    if (!q) return true;
    return [c.titulo, c.tagline, c.nombreCompetencia].some((x) => llano(x).includes(q));
  });
}

/** Los cursos agrupados por competencia, sin grupos vacíos: un título sin nada debajo miente. */
export function porCompetencia(cursos) {
  const grupos = [];
  (cursos || []).forEach((c) => {
    const ya = grupos.find((g) => g.competencia === c.competencia);
    if (ya) ya.cursos.push(c);
    else grupos.push({ competencia: c.competencia, nombreCompetencia: c.nombreCompetencia, cursos: [c] });
  });
  return grupos;
}

/**
 * LOS CURSOS DE UNA COMPETENCIA, AGRUPADOS POR FAMILIA Y CON SU EMOJI.
 *
 * DE DÓNDE SALE. Gabri, 6 de octubre de 2026: *«los de sociolingüística todos llevan el mismo
 * emoji y se agrupan en una misma etiqueta desplegable, de modo que el alumno escoge el que
 * quiere, indicando curso al lado»*. Hasta el 8 de octubre el catálogo enseñaba los veintitantos
 * en fila dentro de cada competencia, y veintitrés filas en Comunicación no son veintitrés
 * opciones: son ninguna. Seis «Gramática» seguidas tampoco son seis cosas distintas, son una
 * con seis niveles.
 *
 * LA FAMILIA NO SE GUARDA EN NINGUNA PARTE: se deduce del título con `familiaDe()`, que es lo
 * que permitió ponerlo sin tocar el CMS ni pedirle un campo nuevo a nadie.
 *
 * Y UN CURSO QUE NO CAE EN NINGUNA FAMILIA SALE IGUAL, en un grupo de uno y sin emoji. Es la
 * misma regla que la de las etapas: **lo que no se reconoce se enseña, no se esconde.** Un
 * título nuevo que nadie ha metido todavía en `emojisDeFamilia.mjs` desaparecería del catálogo
 * sin que ningún error lo dijera, y un curso que no sale no se puede echar de menos.
 *
 * EL ORDEN SE RESPETA Y NO SE REORDENA: los cursos llegan alfabéticos, y eso ya deja cada
 * familia en un solo bloque —lo mide `prueba_catalogo.mjs`—. Reordenar aquí sería una segunda
 * opinión sobre el orden, y dos ordenaciones del mismo sitio acaban discrepando.
 *
 * Y EL TÍTULO SE PIDE, NO SE SUPONE: el catálogo llama `titulo` a lo que el CMS llama `title`,
 * y esta función la usan los dos. Pasar un getter es una línea; **renombrar el campo en el
 * camino es inventarse una tercera forma del mismo curso**, y entonces hay dos sitios donde
 * puede fallar la traducción en vez de uno donde se dice cuál es.
 */
export function porFamilia(cursos, tituloDe = (c) => c.titulo) {
  const grupos = [];
  (cursos || []).forEach((c) => {
    const f = familiaDe(tituloDe(c));
    const ya = f && grupos.find((g) => g.familia === f.familia);
    if (ya) ya.cursos.push(c);
    else grupos.push({
      clave: f ? f.familia : `suelto:${c.id}`,
      familia: f ? f.familia : null,
      // Y SUS OTROS NOMBRES, que los necesita `nivelDe` para no repetirlos en el recuadro.
      tambien: (f && f.tambien) || [],
      emoji: f ? f.emoji : null,
      bloque: (f && f.bloque) || null,
      cursos: [c],
    });
  });
  return grupos;
}

/**
 * LAS FAMILIAS DE UNA COMPETENCIA, BAJO SU RÓTULO: `[{bloque, familias}]`.
 *
 * DE DÓNDE SALE. Gabri, 19 de octubre de 2026: Comunicación junta lo que se estudia en español,
 * la literatura leída en traducción y las lenguas extranjeras, y en una sola rejilla no se
 * distinguía. El bloque es un rótulo, no una tarjeta: una tarjeta más arriba habría escondido
 * los niveles en un tercer piso.
 *
 * EL ORDEN ES EL DE `BLOQUES`, y dentro de cada uno se respeta el que traen las familias. Las
 * familias sin bloque —las de las otras competencias, o un curso que no cae en ninguna— van en
 * un grupo sin rótulo, al final: lo que no se reconoce se enseña igual.
 */
export function porBloque(familias) {
  const fuera = [];
  (familias || []).forEach((f) => {
    const b = f.bloque || null;
    const ya = fuera.find((x) => x.bloque === b);
    if (ya) ya.familias.push(f);
    else fuera.push({ bloque: b, familias: [f] });
  });
  const sitio = (b) => (BLOQUES.indexOf(b) < 0 ? BLOQUES.length : BLOQUES.indexOf(b));
  return fuera.sort((a, b) => sitio(a.bloque) - sitio(b.bloque));
}

/**
 * LO QUE DISTINGUE UN CURSO DE SUS HERMANOS: lo que le sobra al título después de quitarle el
 * nombre de su familia. «Gramática IV» → «IV», «Latín esencial» → «esencial», «Inglés A2» → «A2».
 *
 * Y SE PRUEBAN TODOS LOS NOMBRES DE LA FAMILIA, no sólo el principal. La familia se llama
 * «Ortografía» y sus cursos se titulan «Ortografía I»… pero al revés también pasa: una familia
 * puede llamarse corto y sus cursos largo, o al contrario. Con un solo nombre, el recuadro de
 * «Ortografía I» ponía **«Ortografía I»** entero debajo del título «Ortografía y acentuación»:
 * tres veces la misma palabra en una tarjeta. Gabri, 8 de octubre de 2026: *«aquí deja I, II,
 * III y IV en vez de repetir Ortografía I, II»*.
 *
 * SE QUITA EL NOMBRE MÁS LARGO QUE ENCAJE, por lo mismo que `familiaDe()` elige el más largo:
 * quitar «Ortografía» de «Ortografía y acentuación II» dejaría «y acentuación II».
 *
 * Y SI NO SOBRA NADA SE DEVUELVE EL TÍTULO ENTERO, que es el caso de la familia que se llama
 * igual que su único curso. Una cadena vacía dejaría un recuadro en blanco, y **un hueco no se
 * lee como «no hay nivel», se lee como un fallo**.
 */
export function nivelDe(titulo, familia) {
  const t = String(titulo || "").trim();
  const nombres = (typeof familia === "string" ? [familia] : [
    ...(familia && familia.familia ? [familia.familia] : []),
    ...((familia && familia.tambien) || []),
  ]).filter(Boolean).sort((a, b) => b.length - a.length);
  for (const n of nombres) {
    const resto = t.replace(new RegExp(`^${n}\\s*`, "i"), "").trim();
    if (resto && resto !== t) return resto;
  }
  return t;
}

/**
 * LAS ETAPAS QUE CUBRE UN GRUPO DE CURSOS, sin repetir y en el orden que tengan.
 *
 * PARA QUÉ. Una tarjeta de familia —«Gramática», seis niveles— tiene que decir para quién es, y
 * lo que dice no es una etapa sino **un tramo**: de la del curso más bajo a la del más alto.
 * Quien ordena y escribe el tramo es `etiquetaDeEtapas`, que ya sabe hacerlo con varias claves;
 * aquí sólo se juntan las de todos los cursos del grupo.
 *
 * Y UN GRUPO SIN ETAPAS DEVUELVE LISTA VACÍA, que `etiquetaDeEtapas` convierte en cadena vacía
 * y la tarjeta en no pintar nada. Un curso que no dice para quién es no está diciendo que sea
 * para cualquiera.
 */
export function etapasDe(cursos) {
  const hay = [];
  (cursos || []).forEach((c) => (c.etapa || []).forEach((e) => { if (!hay.includes(e)) hay.push(e); }));
  return hay;
}

/**
 * QUÉ CURSOS ESCOLARES TIENEN ALGO, para no ofrecer un filtro que deja la pantalla en blanco.
 * Un botón que siempre se puede pulsar y a veces no da nada se lee como que la página falla.
 */
export function etapasConCurso(cursos, etapas) {
  const hay = new Set();
  (cursos || []).forEach((c) => c.etapa.forEach((e) => hay.add(e)));
  return (etapas || []).filter((e) => hay.has(e.clave));
}

/** Cuántos cursos hay en total. Se cuenta, no se escribe: es la cifra que la portada afirma. */
export const cuantos = (cursos) => (cursos || []).length;

/**
 * QUÉ SE ENSEÑA, Y ES LA DECISIÓN DE PRODUCTO DE ESTE FICHERO. Vive aquí —y no en el JSX— para
 * que se pueda leer de un vistazo y probar sin navegador: es una regla, no un adorno.
 *
 * SE ENSEÑA TODO, Y DESDE EL 17 DE OCTUBRE DE 2026. Antes había un modo anterior a la lista:
 * la pregunta «¿en qué curso estás?», con la lista detrás de contestarla. Venía del ADR-0014
 * —«se enseña lo mínimo»— y el argumento era bueno: veintidós cursos de golpe no son veintidós
 * opciones, son ninguna. Lo que lo tumbó fue mirar la pantalla ya hecha. Gabri: *«al abrir la
 * sección de cursos no se ven los nombres de las competencias. esto es un problema»*, y *«si
 * pulsamos "ver los 54" se muestran todos los cursos, bien clasificados: esa es la manera en la
 * que se debe mostrar la sección Cursos de forma automática al entrar»*.
 *
 * Y LOS DOS TENÍAN RAZÓN EN SU MOMENTO, que es lo que hay que entender para no deshacer esto en
 * un mes: la regla se escribió cuando la lista era **una lista plana de veintidós filas**, y
 * ahí sí había que guardar. Hoy son cincuenta y cuatro cursos agrupados por competencia, en
 * familias y en tarjetas de color: cincuenta y cuatro cosas clasificadas no se leen como
 * cincuenta y cuatro, se leen como cinco. **Lo que hacía daño no era la cantidad, era la
 * forma.** Una pantalla de cursos cuyo primer contenido es una pregunta esconde justo lo que
 * se viene a ver, y el que llega sin saber qué estudiar es el único al que se le escondía.
 *
 * LO QUE QUEDA: el curso escolar y el buscador siguen siendo filtros, y siguen estando a la
 * vista. Filtrar es quitar de una lista que ya se ve, que es lo contrario de pedir permiso
 * para enseñarla.
 *
 * `etapa` PUEDE VENIR DE LA CUENTA: la plataforma ya sabe en qué curso está el alumno porque se
 * lo preguntó al registrarse, así que empieza por los suyos. Sin cuenta —o sin etapa—, todos.
 */
export function loQueSeEnseña(cursos, { busca = "", etapa = "", competencia = "" } = {}) {
  return { cursos: filtra(cursos, { busca, etapa, competencia }), total: cuantos(cursos) };
}

/**
 * LO QUE MARCÓ EN «QUÉ TE INTERESA» (R5, 24 oct 2026): los cursos de la quinta pantalla de su
 * último test de intereses, sacados de su historial (`GET /test/mine`). Vacío si no lo ha hecho,
 * o si lo hizo antes de que el test tuviera esa pantalla: no se adivina nada que no haya marcado.
 */
export function cursosMarcados(resultados) {
  const suyos = (resultados || []).filter((r) => r && r.test === "interes");
  if (!suyos.length) return [];
  const ultimo = suyos.reduce((a, b) => (String(b.created_at || "") > String(a.created_at || "") ? b : a));
  return Array.isArray(ultimo.grupos_marcados) ? ultimo.grupos_marcados : [];
}

/** Los cursos de las casillas marcadas —«Inglés» trae los seis—, en el orden del catálogo. La
 *  casilla de cada curso la dice `grupoDe()`, la misma regla que pinta el test. */
export function deLoQueMarcaste(cursos, marcados) {
  const suyos = new Set(marcados || []);
  return suyos.size ? (cursos || []).filter((c) => c && suyos.has(claveDe(grupoDe(c.titulo)))) : [];
}

export default todosLosCursos;

